import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import hopscotch from '../../../../../node_modules/hopscotch/dist/js/hopscotch.min.js';
import { Observable, Subject } from 'rxjs';
import { AnalyticsService } from '../../services/analytics.service';

export enum TourName {
	MY = 'analytics-my',
	TEAM = 'analytics-team',
	START = 'analytics-start'
}

export interface TourSteps {
	target: string;
	placement: string;
	title?: string;
	content?: string;
	xOffset?: string;
	yOffset?: string;
	arrowOffset?: string;
	onNext?: any;
	onShow?: any;
	fixedElement?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class TourService {

	public showTourSubject: Subject<any> = new Subject();
	public isTourServiceAvailable: Subject<any> = new Subject();
	private mainTourSteps: TourSteps[];
	private favoriteList = [];

	constructor(private http: HttpClient,
				private translate: TranslateService,
				private analyticsService: AnalyticsService) {

		this.analyticsService.getFavorite().subscribe((response) => {
			this.favoriteList = response ? response.favorites : [];
		});
	}

	public triggerAnalyticsTour(mainTourName: TourName, firstTimeOnTour: boolean) {
		if (!hopscotch.getCurrTour()) {
			this.mainTourSteps = mainTourName === TourName.TEAM ? this.getAnalyticsTeamSteps() : this.getAnalyticsMySteps();
			this.mainTourSteps.push(this.getAnalyticsEndStep(mainTourName));
			this.showTourSubject.next(true);
			this.startTour(TourName.START, mainTourName, firstTimeOnTour);
		}
	}

	public getTourData(tourName: TourName): Observable<any> {
		return this.http.post('platform/tour/queries/getTour', { name : tourName });
	}

	public restartTour(tourName: TourName) {
		this.triggerAnalyticsTour(tourName, false);
	}

	private endTour(mainTourName: TourName, firstTimeOnTour: boolean) {
		if (hopscotch.getCurrTour().id !== TourName.START) {
			this.unhighlightElement(this.mainTourSteps[hopscotch.getCurrStepNum()].target);
		}
		this.showTourSubject.next(false);
		if (firstTimeOnTour) {
			this.http.post('platform/tour/queries/updateTour', { name: mainTourName }).subscribe(() => {});
		}
	}

	private startTour(tourName: TourName, mainTourName: TourName, firstTimeOnTour: boolean) {
		let tour;
		if (tourName === TourName.START) {
			tour = {
				id: 'analytics-start',
				i18n: this.getStartTourTranslations(),
				showCloseButton: true,
				showPrevButton: true,
				onClose: () => {
					this.endTour(mainTourName, firstTimeOnTour);
				},
				onEnd: () => hopscotch.endTour(),
				steps: this.getAnalyticsStartSteps(mainTourName, firstTimeOnTour)
			};
		} else {
			tour = {
				id: 'analytics-main-tour',
				i18n: this.getTranslations(),
				showCloseButton: true,
				onClose: () => {
					this.endTour(mainTourName, firstTimeOnTour);
				},
				onEnd: () => {
					this.endTour(mainTourName, firstTimeOnTour);
					hopscotch.endTour();
				},
				steps: this.mainTourSteps
			};
		}

		hopscotch.startTour(tour, 0);
	}

	private getEndStepContent(mainTourName: TourName) {
		return this.translate.instant('hcm.analytics.end_tour_description',
		{
			analyticType: mainTourName === TourName.MY ?
				this.translate.instant('hcm.analytics.router_my_analytics') :
				this.translate.instant('hcm.analytics.router_team_analytics')
		});
	}

	private getTranslations() {
		return {
			nextBtn: this.translate.instant('hcm.analytics.next_tour_button'),
			prevBtn: this.translate.instant('hcm.analytics.previous_tour_button'),
			doneBtn: this.translate.instant('hcm.analytics.done_tour_button'),
			skipBtn: this.translate.instant('hcm.analytics.skip_tour_button'),
			closeTooltip: this.translate.instant('hcm.analytics.close_tour_tooltip')
		};
	}

	private getStartTourTranslations() {
		return {
			prevBtn: this.translate.instant('hcm.analytics.skip_tour_button'),
			nextBtn: this.translate.instant('hcm.analytics.start_tour_button'),
			closeTooltip: this.translate.instant('hcm.analytics.close_tour_tooltip'),
			stepNums: ['', '1']
		};
	}

	private getAnalyticsStartSteps(mainTourName: TourName, firstTimeOnTour: boolean) {
		return [
			{
				target: '.sds-page-title',
				placement: 'bottom',
				onShow: () => hopscotch.showStep(1)
			},
			{
				target: '.sds-page-title',
				placement: 'bottom',
				showSkip: false,
				content: mainTourName === TourName.MY ? this.translate.instant('hcm.analytics.start_my_tour_description') : this.translate.instant('hcm.analytics.start_team_tour_description'),
				onPrev: () => {
					hopscotch.endTour();
					this.showTourSubject.next(false);
				},
				onNext: () => {
					hopscotch.resetDefaultI18N();
					hopscotch.endTour();
					this.startTour(mainTourName, mainTourName, firstTimeOnTour);
				}
			},
			{
				target: '#tour-darker-screen',
				placement: 'bottom'
			}
		];
	}

	private getAnalyticsEndStep(mainTourName: TourName) {
		return {
			target: '#tour-darker-screen',
			placement: 'bottom',
			xOffset: 'center',
			yOffset: 'center',
			arrowOffset: '100000',
			fixedElement: true,
			content: this.getEndStepContent(mainTourName)
		};
	}

	private getAnalyticsMySteps() {
		const tourSteps: TourSteps[] = [
			{
				target: '#header-analytics-search',
				placement: 'left',
				title: this.translate.instant('hcm.analytics.search_button_tour_title'),
				content: this.translate.instant('hcm.analytics.search_button_tour_description'),
				onShow: () => this.highlightElement('#header-analytics-search'),
				onNext: () => this.unhighlightElement('#header-analytics-search')
			},
			{
				target: '#contracts',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.contracts_button_tour_title'),
				content: this.translate.instant('hcm.analytics.contracts_button_tour_description'),
				onShow: () => this.highlightElement('#contracts'),
				onNext: () => this.unhighlightElement('#contracts')
			},
			{
				target: '#header-analytics-menu',
				placement: 'left',
				title: this.translate.instant('hcm.analytics.analytics_menu_tour_title'),
				content: this.translate.instant('hcm.analytics.analytics_menu_tour_description'),
				onShow: () => this.highlightElement('#header-analytics-menu'),
				onNext: () => this.unhighlightElement('#header-analytics-menu')
			},
			{
				target: '.ui-breadcrumb',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.breadcrumb_tour_title'),
				content: this.translate.instant('hcm.analytics.breadcrumb_tour_description'),
				onShow: () => this.changeHighlightBreadcrumb(),
				onNext: () => this.changeHighlightBreadcrumb()
			},
			{
				target: '.summary-container',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.my_profile_tour_title'),
				content: this.translate.instant('hcm.analytics.my_profile_tour_description'),
				onShow: () => this.highlightElement('.summary-container'),
				onNext: () => this.unhighlightElement('.summary-container')
			},
			{
				target: '.my-team-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.my_team_tour_title'),
				content: this.translate.instant('hcm.analytics.my_team_tour_description'),
				onShow: () => {
					this.clickOnElement('.my-team-tab');
					this.highlightElement('.my-team-tab');
				},
				onNext: () => this.unhighlightElement('.my-team-tab')
			},
			{
				target: '.my-appointment-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.my_appointment_tour_title'),
				content: this.translate.instant('hcm.analytics.my_appointment_tour_description'),
				onShow: () => {
					this.clickOnElement('.my-appointment-tab');
					this.highlightElement('.my-appointment-tab');
				},
				onNext: () => this.unhighlightElement('.my-appointment-tab')
			},
			{
				target: '.my-payroll-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.my_payroll_tour_title'),
				content: this.translate.instant('hcm.analytics.my_payroll_tour_description'),
				onShow: () => {
					this.clickOnElement('.my-payroll-tab');
					this.highlightElement('.my-payroll-tab');
				},
				onNext: () => this.unhighlightElement('.my-payroll-tab')
			}];

			const payrollTabVisible = document.getElementsByClassName('my-payroll-tab')[0];

			if (!this.isFavorite('hcm-payroll-payslip') && payrollTabVisible) {
				tourSteps.push(
					{
						target: '#btn-drill-payslip-panel',
						placement: 'left',
						title: this.translate.instant('hcm.analytics.detail_tour_title'),
						content: this.translate.instant('hcm.analytics.detail_tour_description'),
						onShow: () => {
							this.highlightElement('#btn-drill-payslip-panel');
						},
						onNext: () => this.unhighlightElement('#btn-drill-payslip-panel')
					});
			}
			if (!this.isFavorite('hcm-payroll-my-vacation')) {
				tourSteps.push(
					{
						target: '#btn-favorite-my-vacation-panel',
						placement: 'left',
						title: this.translate.instant('hcm.analytics.favorite_tour_title'),
						content: this.translate.instant('hcm.analytics.favorite_tour_description'),
						onShow: () => this.highlightElement('#btn-favorite-my-vacation-panel'),
						onNext: () => {
							this.unhighlightElement('#btn-favorite-my-vacation-panel');
							this.clickOnElement('#btn-favorite-my-vacation-panel');
						}
					},
					{
						target: '.my-resume-tab',
						placement: 'top',
						title: this.translate.instant('hcm.analytics.favorite_tour_title'),
						content: this.translate.instant('hcm.analytics.favorite_2_tour_description'),
						onShow: () => {
							this.clickOnElement('.my-resume-tab');
							this.highlightElement('.my-resume-tab');
						},
						onNext: () => this.unhighlightElement('.my-resume-tab')
					}
				);
			}

			tourSteps.push(
				{
					target: '.my-performance-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.my_performance_tour_title'),
					content: this.translate.instant('hcm.analytics.my_performance_tour_description'),
					onShow: () => {
						this.clickOnElement('.my-performance-tab');
						this.highlightElement('.my-performance-tab');
					},
					onNext: () => this.unhighlightElement('.my-performance-tab')
				},
				{
					target: '.my-career-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.my_career_tour_title'),
					content: this.translate.instant('hcm.analytics.my_career_tour_description'),
					onShow: () => {
						this.clickOnElement('.my-career-tab');
						this.highlightElement('.my-career-tab');
					},
					onNext: () => this.unhighlightElement('.my-career-tab')
				},
				{
					target: '.corporate-wall-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.corporate_wall_tour_title'),
					content: this.translate.instant('hcm.analytics.corporate_wall_tour_description'),
					onShow: () => {
						this.clickOnElement('.corporate-wall-tab');
						this.highlightElement('.corporate-wall-tab');
					},
					onNext: () => this.unhighlightElement('.corporate-wall-tab')
				}
			);

		return tourSteps;
	}

	private getAnalyticsTeamSteps() {
		const tourSteps: TourSteps[] = [
			{
				target: '#header-analytics-search',
				placement: 'left',
				title: this.translate.instant('hcm.analytics.search_button_tour_title'),
				content: this.translate.instant('hcm.analytics.search_button_tour_description'),
				onShow: () => this.highlightElement('#header-analytics-search'),
				onNext: () => this.unhighlightElement('#header-analytics-search')
			},
			{
				target: '#contracts',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.contracts_button_tour_title'),
				content: this.translate.instant('hcm.analytics.contracts_button_tour_description'),
				onShow: () => this.highlightElement('#contracts'),
				onNext: () => this.unhighlightElement('#contracts')
			},
			{
				target: '#header-analytics-menu',
				placement: 'left',
				title: this.translate.instant('hcm.analytics.analytics_menu_tour_title'),
				content: this.translate.instant('hcm.analytics.analytics_menu_tour_description'),
				onShow: () => this.highlightElement('#header-analytics-menu'),
				onNext: () => this.unhighlightElement('#header-analytics-menu')
			},
			{
				target: '.ui-breadcrumb',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.breadcrumb_tour_title'),
				content: this.translate.instant('hcm.analytics.breadcrumb_tour_description'),
				onShow: () => this.changeHighlightBreadcrumb(),
				onNext: () => this.changeHighlightBreadcrumb()
			},
			{
				target: '.summary-container',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.my_profile_tour_title'),
				content: this.translate.instant('hcm.analytics.my_profile_tour_description'),
				onShow: () => this.highlightElement('.summary-container'),
				onNext: () => this.unhighlightElement('.summary-container')
			},
			{
				target: '#filterButton',
				placement: 'bottom',
				title: this.translate.instant('hcm.analytics.filter_team_tour_title'),
				content: this.translate.instant('hcm.analytics.filter_team_tour_description'),
				onShow: () => this.highlightElement('#filterButton'),
				onNext: () => this.unhighlightElement('#filterButton')
			},
			{
				target: '.team-team-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.team_team_tour_title'),
				content: this.translate.instant('hcm.analytics.team_team_tour_description'),
				onShow: () => {
					this.clickOnElement('.team-team-tab');
					this.highlightElement('.team-team-tab');
				},
				onNext: () => this.unhighlightElement('.team-team-tab')
			},
			{
				target: '.team-features-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.team_features_tour_title'),
				content: this.translate.instant('hcm.analytics.team_features_tour_description'),
				onShow: () => {
					this.clickOnElement('.team-features-tab');
					this.highlightElement('.team-features-tab');
				},
				onNext: () => this.unhighlightElement('.team-features-tab')
			},
			{
				target: '.team-appointment-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.team_appointment_tour_title'),
				content: this.translate.instant('hcm.analytics.team_appointment_tour_description'),
				onShow: () => {
					this.clickOnElement('.team-appointment-tab');
					this.highlightElement('.team-appointment-tab');
				},
				onNext: () => this.unhighlightElement('.team-appointment-tab')
			},
			{
				target: '.team-payroll-tab',
				placement: 'top',
				title: this.translate.instant('hcm.analytics.team_payroll_tour_title'),
				content: this.translate.instant('hcm.analytics.team_payroll_tour_description'),
				onShow: () => {
					this.clickOnElement('.team-payroll-tab');
					this.highlightElement('.team-payroll-tab');
				},
				onNext: () => this.unhighlightElement('.team-payroll-tab')
			}];

			const payrollTabVisible = document.getElementsByClassName('team-payroll-tab')[0];
			if (!this.isFavorite('hcm-payroll-vacation') && payrollTabVisible) {
				tourSteps.push(
					{
						target: '#btn-drill-vacation-3b7c6618',
						placement: 'top',
						arrowOffset: '250',
						xOffset: '-200',
						title: this.translate.instant('hcm.analytics.detail_tour_title'),
						content: this.translate.instant('hcm.analytics.detail_tour_description'),
						onShow: () => {
							this.highlightElement('#btn-drill-vacation-3b7c6618');
						},
						onNext: () => this.unhighlightElement('#btn-drill-vacation-3b7c6618')
					},
					{
						target: '#btn-favorite-vacation-3b7c6618',
						placement: 'left',
						title: this.translate.instant('hcm.analytics.favorite_tour_title'),
						content: this.translate.instant('hcm.analytics.favorite_tour_description'),
						onShow: () => this.highlightElement('#btn-favorite-vacation-3b7c6618'),
						onNext: () => {
							this.unhighlightElement('#btn-favorite-vacation-3b7c6618');
							this.clickOnElement('#btn-favorite-vacation-3b7c6618');
						}
					},
					{
						target: '.team-resume-tab',
						placement: 'top',
						title: this.translate.instant('hcm.analytics.favorite_tour_title'),
						content: this.translate.instant('hcm.analytics.favorite_2_tour_description'),
						onShow: () => {
							this.clickOnElement('.team-resume-tab');
							this.highlightElement('.team-resume-tab');
						},
						onNext: () => this.unhighlightElement('.team-resume-tab')
					}
				);
			}

			tourSteps.push(
				{
					target: '.team-performance-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.team_performance_tour_title'),
					content: this.translate.instant('hcm.analytics.team_performance_tour_description'),
					onShow: () => {
						this.clickOnElement('.team-performance-tab');
						this.highlightElement('.team-performance-tab');
					},
					onNext: () => this.unhighlightElement('.team-performance-tab')
				},
				{
					target: '.team-career-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.team_career_tour_title'),
					content: this.translate.instant('hcm.analytics.team_career_tour_description'),
					onShow: () => {
						this.clickOnElement('.team-career-tab');
						this.highlightElement('.team-career-tab');
					},
					onNext: () => this.unhighlightElement('.team-career-tab')
				},
				{
					target: '.team-recruitment-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.recruitment_tour_title'),
					content: this.translate.instant('hcm.analytics.team_recruitment_tour_description'),
					onShow: () => {
						this.clickOnElement('.team-recruitment-tab');
						this.highlightElement('.team-recruitment-tab');
					},
					onNext: () => this.unhighlightElement('.team-recruitment-tab')
				},
				{
					target: '.team-remuneration-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.remuneration_tour_title'),
					content: this.translate.instant('hcm.analytics.team_remuneration_tour_description'),
					onShow: () => {
						this.clickOnElement('.team-remuneration-tab');
						this.highlightElement('.team-remuneration-tab');
					},
					onNext: () => this.unhighlightElement('.team-remuneration-tab')
				},
				{
					target: '.team-moods-tab',
					placement: 'top',
					title: this.translate.instant('hcm.analytics.moods_tour_title'),
					content: this.translate.instant('hcm.analytics.team_moods_tour_description'),
					onShow: () => {
						this.clickOnElement('.team-moods-tab');
						this.highlightElement('.team-moods-tab');
					},
					onNext: () => this.unhighlightElement('.team-moods-tab')
				}
			);

		return tourSteps;
	}

	private isFavorite(outletId: string) {
		if (this.favoriteList && this.favoriteList.length) {
			return this.favoriteList.filter(module => module.dashboards ? module.dashboards.filter((val) => val.outletIdName === outletId)[0] : [])[0];
		}
		return false;
	}

	private clickOnElement(targetSelector: string) {
		if (targetSelector) {
			if (targetSelector.startsWith('#')) {
				document.getElementById(targetSelector.substr(1, targetSelector.length)).click();
			}
			if (targetSelector.startsWith('.')) {
				(document.getElementsByClassName(targetSelector.substr(1, targetSelector.length))[0] as HTMLElement).click();
			}
		}
	}

	private highlightElement(element: string) {
		(document.getElementsByClassName('header')[0] as HTMLElement).style.position = 'initial';
		if (element) {
			let domElement;
			if (element.startsWith('#')) {
				domElement = document.getElementById(element.substr(1, element.length));
			} else if (element.startsWith('.')) {
				domElement = document.getElementsByClassName(element.substr(1, element.length))[0] as HTMLElement;
			}

			domElement.style.zIndex = '100001';
			domElement.style.backgroundColor = 'white';
			domElement.style.pointerEvents = 'none';

			if (element && element.endsWith('tab')) {
				element.includes('features') ? this.changeHighlightInnerPanel() : this.changeHighlightPanel();
			}
		}
	}

	private unhighlightElement(element: string) {
		(document.getElementsByClassName('header')[0] as HTMLElement).style.position = '';
		if (element) {
			let domElement;
			if (element.startsWith('#')) {
				domElement = document.getElementById(element.substr(1, element.length));
			} else if (element.startsWith('.')) {
				domElement = document.getElementsByClassName(element.substr(1, element.length))[0] as HTMLElement;
			}
			domElement.style.zIndex = '';
			domElement.style.backgroundColor = '';
			domElement.style.pointerEvents = '';
		}

		if (element && element.endsWith('tab')) {
			element.includes('features') ? this.changeHighlightInnerPanel() : this.changeHighlightPanel();
		}
	}

	private changeHighlightPanel() {
		this.changePanelAndBreadcrumbStyles(this.getPanelElement());
	}

	private changeHighlightInnerPanel() {
		this.changePanelAndBreadcrumbStyles(this.getPanelElement(1));
	}

	private changeHighlightBreadcrumb() {
		this.changePanelAndBreadcrumbStyles(this.getBreadcrumbElement());
	}

	private getPanelElement(index = 0) {
		return document.getElementsByClassName('ui-tabview-panels')[index] as HTMLElement;
	}

	private getBreadcrumbElement() {
		return document.getElementsByClassName('ui-breadcrumb')[0] as HTMLElement;
	}

	private changePanelAndBreadcrumbStyles(domElement) {
		if (domElement) {
			if (domElement.style.pointerEvents) {
				domElement.style.zIndex = '';
				domElement.style.backgroundColor = '';
				domElement.style.position = '';
				domElement.style.pointerEvents = '';
			} else {
				domElement.style.zIndex = '100001';
				domElement.style.backgroundColor = 'white';
				domElement.style.position = 'relative';
				domElement.style.pointerEvents = 'none';
			}
		}
	}
}
